import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Radio
} from '@mui/material';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import BookOutlinedIcon from '@mui/icons-material/BookOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';

import { CustomBreadcrumbs, PageBanner, LatexRenderer } from '../../common';
import teacherBannerBg from '../../../assets/images/teacher.jpg';
import examService from '../../../infrastructure/services/examService';

const getPointsStyle = (marksAwarded, totalMarks) => {
  const awarded = Number(marksAwarded) || 0;
  const total = Number(totalMarks) || 0;

  if (total === 0) {
    return {
      bg: '#f1f5f9', // light gray
      color: '#64748b' // slate gray
    };
  }

  if (awarded === 0) {
    return {
      bg: '#fef2f2', // light red
      color: '#ef4444' // red
    };
  }

  if (awarded === total) {
    return {
      bg: '#ecfdf5', // light green
      color: '#10b981' // green
    };
  }

  // Partially correct
  return {
    bg: '#fffbeb', // light orange
    color: '#f59e0b' // orange
  };
};

const getGradingSummaryTheme = (grade, percentage) => {
  const score = Number(percentage) || 0;
  const g = String(grade || '').toUpperCase();

  if (g === 'F' || score < 40) {
    return {
      bg: 'linear-gradient(180deg, rgba(250, 82, 82, 0.15) 0%, rgba(255, 255, 255, 0.15) 93.95%)',
      color: '#fa5252',
      border: '#ffa8a8',
      barTrack: 'rgba(250, 82, 82, 0.15)',
      shadow: 'rgba(250, 82, 82, 0.05)',
      improvementColor: '#fa5252'
    };
  }

  if (g === 'C' || g === 'D' || g === 'S' || (score >= 40 && score < 75)) {
    return {
      bg: 'linear-gradient(180deg, rgba(255, 161, 117, 0.15) 0%, rgba(255, 255, 255, 0.15) 93.95%)',
      color: '#f59e0b',
      border: '#FF9F59',
      barTrack: 'rgba(245, 158, 11, 0.15)',
      shadow: 'rgba(245, 158, 11, 0.05)',
      improvementColor: '#d97706'
    };
  }

  return {
    bg: 'linear-gradient(180deg, rgba(152, 255, 100, 0.15) 0%, rgba(255, 255, 255, 0.15) 93.95%)',
    color: '#10b981',
    border: '#4EB860',
    barTrack: 'rgba(16, 185, 129, 0.15)',
    shadow: 'rgba(16, 185, 129, 0.05)',
    improvementColor: '#d97706'
  };
};

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<\/?[^>]+(>|$)/g, "");
};

export default function AnswerBreakdownPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { classExamId: paramExamId } = useParams();

  const stateExam = location.state?.exam || "";
  const classExamId = paramExamId || stateExam?.id || stateExam?.examId;
  const stateResults = location.state?.results || null;

  const rawExamId = stateExam?.examId || new URLSearchParams(location.search).get('examId');
  const examId = rawExamId && rawExamId !== classExamId ? rawExamId : undefined;

  const [examResults, setExamResults] = useState(stateResults);
  const [loading, setLoading] = useState(!stateResults || !stateResults.gradingSummary);

  useEffect(() => {
    const fetchResultData = async () => {
      if (!classExamId) {
        setLoading(false);
        return;
      }
      try {
        const res = await examService.getExamResult(classExamId, examId);
        if (res && res.isSuccessful && res.content) {
          setExamResults(res.content);
        }
      } catch (err) {
        console.error("Failed to load results in AnswerBreakdownPage:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchResultData();
  }, [classExamId, examId]);

  const activeExam = examResults?.exam || examResults?.examMetadata || stateExam || {};
  const activeQuestions = examResults?.questions || examResults?.detailedResults || stateResults?.questions || [];

  const gradingSummary = examResults?.gradingSummary || {};
  const scorePercentage = gradingSummary.percentage ?? stateResults?.scorePercentage ?? 0;
  const gradeLetter = gradingSummary.grade ?? '-';
  const totalMarksAwarded = gradingSummary.totalMarksAwarded ?? (activeQuestions.reduce((sum, q) => sum + (q.marksAwarded ?? 0), 0)) ?? 0;
  const totalMarksPossible = gradingSummary.totalMarksPossible ?? activeExam.totalMarks ?? 100;
  const strengths = gradingSummary.strengths || [];
  const improvements = gradingSummary.improvements || [];
  const summaryTheme = getGradingSummaryTheme(gradeLetter, scorePercentage);

  const examTitle = stripHtml(activeExam.title || activeExam.name || 'General Exam');
  const examSubject = stripHtml(activeExam.subject || activeExam.subjectName || 'General Subject');
  const examGrade = stripHtml(activeExam.grade || activeExam.gradeName || '');
  const examDuration = stripHtml(activeExam.duration || '3h 0m');

  const displayQuestions = activeQuestions || [];

  const handleBack = () => {
    navigate(`/exam-manager/exam-summary/${classExamId}`, {
      state: {
        exam: activeExam,
        isCompleted: true,
        results: {
          ...stateResults,
          ...examResults,
          questions: activeQuestions
        }
      }
    });
  };

  const breadcrumbItems = [
    { label: 'Home', href: '/dashboard' },
    { label: 'Exam Management', href: '/exam-manager' },
    { label: 'Exam Summary', onClick: (e) => { e.preventDefault(); handleBack(); } },
    { label: 'Answer Breakdown' }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 15 }}>
        <CircularProgress sx={{ color: '#FF502D' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 5, fontFamily: 'Poppins, sans-serif' }}>
      {/* Breadcrumbs */}
      <CustomBreadcrumbs items={breadcrumbItems} sx={{ mb: 2 }} />

      {/* Page Banner */}
      <PageBanner
        title="Answer Breakdown"
        description={`${examSubject} - ${examGrade || 'General'}`}
        backgroundImage={teacherBannerBg}
      >
        <Button
          variant="outlined"
          onClick={handleBack}
          sx={{
            borderColor: '#ffffff',
            borderWidth: '1px',
            color: '#ffffff',
            borderRadius: '14px',
            px: 3,
            py: 0.8,
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(4px)',
            transition: 'all 0.25s ease',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            '&:hover': {
              borderColor: '#ffffff',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'translateX(-2px)'
            }
          }}
        >
          <KeyboardArrowLeftRoundedIcon sx={{ fontSize: '18px' }} /> Back
        </Button>
      </PageBanner>

      {/* Top Section: Exam Details & Grading Summary cards */}
      <Box
        sx={{
          mt: 4,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3.5,
          width: '100%'
        }}
      >
        {/* Left Card: Exam Details */}
        <Paper
          elevation={0}
          sx={{
            p: '6px',
            border: '1.5px solid #A78BFA',
            borderRadius: '20px',
            bgcolor: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: '0 10px 20px rgba(139, 92, 246, 0.05)'
            }
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(180deg, rgba(150, 100, 255, 0.15) 0%, rgba(255, 255, 255, 0.15) 93.95%)',
              borderRadius: '12px',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              flexGrow: 1,
              boxSizing: 'border-box'
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 850, fontSize: '17px', color: '#1e293b', fontFamily: 'Poppins', mb: 3 }}>
                Exam Details
              </Typography>

              <Stack spacing={2.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ bgcolor: '#e0e7ff', width: 40, height: 40, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <AssignmentOutlinedIcon sx={{ color: '#4f46e5', fontSize: '20px' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '11.5px', color: '#64748b', fontWeight: 650, textTransform: 'uppercase', tracking: '0.5px' }}>
                      Exam Title
                    </Typography>
                    <Typography sx={{ fontSize: '14.5px', color: '#1e293b', fontWeight: 700, fontFamily: 'Poppins' }}>
                      {examTitle}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ bgcolor: '#e0f7fa', width: 40, height: 40, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <BookOutlinedIcon sx={{ color: '#00838f', fontSize: '20px' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '11.5px', color: '#64748b', fontWeight: 650, textTransform: 'uppercase', tracking: '0.5px' }}>
                      Subject & Grade
                    </Typography>
                    <Typography sx={{ fontSize: '14.5px', color: '#1e293b', fontWeight: 700, fontFamily: 'Poppins' }}>
                      {examSubject} {examGrade ? `(${examGrade})` : ''}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ bgcolor: '#fff8e1', width: 40, height: 40, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <AccessTimeOutlinedIcon sx={{ color: '#ff8f00', fontSize: '20px' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '11.5px', color: '#64748b', fontWeight: 650, textTransform: 'uppercase', tracking: '0.5px' }}>
                      Duration
                    </Typography>
                    <Typography sx={{ fontSize: '14.5px', color: '#1e293b', fontWeight: 700, fontFamily: 'Poppins' }}>
                      {examDuration}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Paper>

        {/* Right Card: Grading Summary */}
        <Paper
          elevation={0}
          sx={{
            p: '6px',
            border: `1.5px solid ${summaryTheme.border}`,
            borderRadius: '20px',
            bgcolor: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: `0 10px 20px ${summaryTheme.shadow}`
            }
          }}
        >
          <Box
            sx={{
              background: summaryTheme.bg,
              borderRadius: '12px',
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              flexGrow: 1,
              boxSizing: 'border-box'
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 850, fontSize: '17px', color: '#1e293b', fontFamily: 'Poppins', mb: 2 }}>
                Grading Summary
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{ fontSize: '46px', fontWeight: 800, color: summaryTheme.color, fontFamily: 'Poppins', lineHeight: 1 }}>
                    {scorePercentage}%
                  </Typography>
                  <Chip
                    label={`Grade: ${gradeLetter}`}
                    sx={{
                      bgcolor: summaryTheme.color,
                      color: '#ffffff',
                      fontWeight: 750,
                      fontFamily: 'Poppins',
                      fontSize: '12px',
                      borderRadius: '8px',
                      height: '24px'
                    }}
                  />
                </Box>

                <Box sx={{ flexGrow: 1, minWidth: '150px' }}>
                  <Typography sx={{ fontSize: '12.5px', color: '#475569', fontWeight: 700, mb: 0.5, fontFamily: 'Poppins' }}>
                    Total Score: {totalMarksAwarded}/{totalMarksPossible} Points
                  </Typography>
                  <Box sx={{ height: '7px', bgcolor: summaryTheme.barTrack, borderRadius: '4px', overflow: 'hidden' }}>
                    <Box sx={{ width: `${(totalMarksAwarded / totalMarksPossible) * 100}%`, bgcolor: summaryTheme.color, height: '100%' }} />
                  </Box>
                </Box>
              </Box>

              {/* Strengths & Improvements lists */}
              {(strengths.length > 0 || improvements.length > 0) && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, borderTop: `1px solid ${summaryTheme.border}`, pt: 2.5 }}>
                  {strengths.length > 0 && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                        <ThumbUpOutlinedIcon sx={{ color: '#10b981', fontSize: '15px' }} />
                        <Typography sx={{ fontSize: '12.5px', color: '#10b981', fontWeight: 750, fontFamily: 'Poppins' }}>Strengths</Typography>
                      </Box>
                      <Stack spacing={0.5}>
                        {strengths.map((str, idx) => (
                          <Typography key={idx} sx={{ fontSize: '12px', color: '#334155', fontFamily: 'Poppins', lineHeight: 1.45 }}>
                            • {str}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {improvements.length > 0 && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                        <TrendingUpOutlinedIcon sx={{ color: summaryTheme.improvementColor, fontSize: '15px' }} />
                        <Typography sx={{ fontSize: '12.5px', color: summaryTheme.improvementColor, fontWeight: 750, fontFamily: 'Poppins' }}>To Improve</Typography>
                      </Box>
                      <Stack spacing={0.5}>
                        {improvements.map((imp, idx) => (
                          <Typography key={idx} sx={{ fontSize: '12px', color: '#334155', fontFamily: 'Poppins', lineHeight: 1.45 }}>
                            • {imp}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Accordion Questions List */}
      <Stack spacing={2} sx={{ mt: 5, width: '100%' }}>
        {displayQuestions.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              border: '1.5px dashed #cbd5e1',
              borderRadius: '20px',
              bgcolor: '#ffffff',
              textAlign: 'center'
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '16px',
                bgcolor: '#f8fafc',
                color: '#94a3b8',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1.5
              }}
            >
              <AssignmentOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Typography sx={{ fontSize: '16px', fontWeight: 750, color: '#1e293b', fontFamily: 'Poppins', mb: 0.5 }}>
              No answer breakdown records found
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#64748b', fontFamily: 'Poppins' }}>
              There are no submitted answers or marking details available for this exam yet.
            </Typography>
          </Paper>
        ) : displayQuestions.map((q, idx) => {
          const isQuestionEssay = Array.isArray(q.markingBreakdown) && q.markingBreakdown.length > 0;

          if (!isQuestionEssay) {
            // Render MCQ Layout using Accordion
            const rawStudentAns = q.studentAnswer !== undefined && q.studentAnswer !== null
              ? q.studentAnswer
              : (stateResults?.answers?.[idx] !== undefined ? stateResults?.answers?.[idx] : null);
            const studentAns = rawStudentAns !== null && rawStudentAns !== undefined ? String(rawStudentAns) : null;
            const isAnswered = studentAns !== null && studentAns !== '';
            const isCorrect = q.isCorrect !== undefined
              ? q.isCorrect
              : (isAnswered && studentAns.trim().toLowerCase() === (q.correctAnswer || '').toString().trim().toLowerCase());

            const qMarksAwarded = q.marksAwarded !== undefined && q.marksAwarded !== null
              ? q.marksAwarded
              : (isCorrect ? (q.totalMarks ?? q.maxMarks ?? 1) : 0);
            const qTotalMarks = q.totalMarks ?? q.maxMarks ?? 1;

            const questionIndexStr = (idx + 1).toString().padStart(2, '0');

            let feedbackText = q.feedback || q.overallFeedback || '';
            if (!feedbackText) {
              if (isCorrect) {
                feedbackText = `Correct - Your selected option: "${studentAns}"`;
              } else if (isAnswered) {
                feedbackText = `Incorrect - You selected "${studentAns}" instead of "${q.correctAnswer}"`;
              } else {
                feedbackText = `Incorrect - You skipped this question. The correct answer is "${q.correctAnswer}".`;
              }
            }

            return (
              <Accordion
                key={q.questionId || q.id || idx}
                sx={{
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '20px !important',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  overflow: 'hidden',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    borderColor: '#cbd5e1',
                    boxShadow: '0 4px 15px rgba(148, 163, 184, 0.05)'
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#64748b' }} />}
                  sx={{
                    px: { xs: 2.5, sm: 3.5 },
                    py: 1,
                    bgcolor: '#ffffff',
                    minHeight: '64px',
                    '& .MuiAccordionSummary-content': {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      flexWrap: 'wrap',
                      gap: 2
                    }
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mr: 'auto', flexWrap: 'wrap', gap: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1e293b', fontFamily: 'Poppins' }}>
                      Question {questionIndexStr}
                    </Typography>
                    {/* Given Answer Badge */}
                    <Chip
                      label={isAnswered ? `Answer ${studentAns}` : 'Skipped'}
                      variant="outlined"
                      sx={{
                        color: isAnswered ? (isCorrect ? '#10b981' : '#ef4444') : '#64748b',
                        borderColor: isAnswered ? (isCorrect ? '#a7f3d0' : '#fca5a5') : '#cbd5e1',
                        bgcolor: isAnswered ? (isCorrect ? '#ecfdf5' : '#fef2f2') : '#f8fafc',
                        fontWeight: 650,
                        fontFamily: 'Poppins',
                        fontSize: '12px',
                        height: '28px'
                      }}
                    />
                  </Stack>

                  {/* Points Badge */}
                  <Chip
                    label={`${qMarksAwarded}/${qTotalMarks} Points`}
                    sx={{
                      bgcolor: getPointsStyle(qMarksAwarded, qTotalMarks).bg,
                      color: getPointsStyle(qMarksAwarded, qTotalMarks).color,
                      fontWeight: 700,
                      fontFamily: 'Poppins',
                      fontSize: '12px',
                      height: '28px',
                      px: 0.5,
                      mr: 1
                    }}
                  />
                </AccordionSummary>

                <AccordionDetails sx={{ px: { xs: 2.5, sm: 4 }, pb: 4, pt: 1, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                  {/* Question Text */}
                  <Typography
                    component="div"
                    className="exam-rich-text"
                    sx={{
                      fontWeight: 600,
                      fontSize: '15px',
                      color: '#1e293b',
                      fontFamily: 'Poppins',
                      mb: 3,
                      lineHeight: 1.55,
                      mt: 2
                    }}
                  >
                    {q.questionType === 'match-two-sides' && (
                      <Typography sx={{ mb: 1.5, fontWeight: 700, fontSize: '15px', color: '#475569', fontFamily: 'Poppins' }}>
                        Match two sides question :
                      </Typography>
                    )}
                    <LatexRenderer text={q.questionText || q.text || ''} />
                  </Typography>

                  {q.questionType === 'table' && q.tableJson && (() => {
                    const rowKeys = Object.keys(q.tableJson)
                      .filter(key => key.startsWith('row'))
                      .sort((a, b) => {
                        const numA = parseInt(a.replace('row', ''), 10);
                        const numB = parseInt(b.replace('row', ''), 10);
                        return numA - numB;
                      });

                    const firstRowKey = rowKeys[0];
                    const colKeys = firstRowKey
                      ? Object.keys(q.tableJson[firstRowKey])
                        .filter(key => key.startsWith('col'))
                        .sort((a, b) => {
                          const numA = parseInt(a.replace('col', ''), 10);
                          const numB = parseInt(b.replace('col', ''), 10);
                          return numA - numB;
                        })
                      : [];

                    return (
                      <Box className="exam-rich-text" sx={{ mt: 2, mb: 3 }}>
                        <Typography sx={{ fontSize: '13px', color: '#64748b', fontWeight: 655, mb: 1.5, fontFamily: 'Poppins' }}>
                          Question Table Options:
                        </Typography>
                        <Box sx={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid #cbd5e1' }}>
                          <table style={{ margin: 0, width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ backgroundColor: '#f8fafc' }}>
                                <th style={{ width: '80px', textAlign: 'center', borderBottom: '2px solid #cbd5e1', padding: '12px 14px', fontWeight: 700 }}>Select</th>
                                {colKeys.map((colKey) => {
                                  const headerVal = q.tableJson[firstRowKey]?.[colKey]?.header || '';
                                  return (
                                    <th key={colKey} style={{ borderBottom: '2px solid #cbd5e1', padding: '12px 14px', textAlign: 'left', fontWeight: 700 }}>
                                      <LatexRenderer text={headerVal} />
                                    </th>
                                  );
                                })}
                                <th style={{ width: '140px', textAlign: 'center', borderBottom: '2px solid #cbd5e1', padding: '12px 14px', fontWeight: 700 }}>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rowKeys.map((rowKey) => {
                                const rowNumber = parseInt(rowKey.replace('row', ''), 10);
                                const isStudentSelected = String(studentAns) === String(rowNumber);
                                const isCorrectRow = String(q.correctAnswer) === String(rowNumber);

                                let rowBgColor = '#ffffff';
                                if (isCorrectRow) {
                                  rowBgColor = '#ecfdf5';
                                } else if (isStudentSelected && !isCorrectRow) {
                                  rowBgColor = '#fef2f2';
                                }

                                return (
                                  <tr key={rowKey} style={{ backgroundColor: rowBgColor }}>
                                    <td style={{ textAlign: 'center', padding: '10px 14px', borderTop: '1px solid #cbd5e1' }}>
                                      <Radio
                                        checked={isStudentSelected}
                                        disabled
                                        sx={{ p: 0, '&.Mui-checked': { color: isCorrectRow ? '#10b981' : '#ef4444' } }}
                                      />
                                    </td>
                                    {colKeys.map((colKey) => {
                                      const cellVal = q.tableJson[rowKey]?.[colKey]?.value || '';
                                      return (
                                        <td key={colKey} style={{ padding: '10px 14px', borderTop: '1px solid #cbd5e1', textAlign: 'left' }}>
                                          <LatexRenderer text={cellVal} />
                                        </td>
                                      );
                                    })}
                                    <td style={{ textAlign: 'center', padding: '10px 14px', borderTop: '1px solid #cbd5e1', fontWeight: 700, fontSize: '12px' }}>
                                      {isCorrectRow && (
                                        <span style={{ color: '#10b981' }}>Correct Answer</span>
                                      )}
                                      {isStudentSelected && !isCorrectRow && (
                                        <span style={{ color: '#ef4444' }}>Your Selection</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </Box>
                      </Box>
                    );
                  })()}


                  {/* Box 1: Your Answer */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      border: `1.5px solid ${isCorrect ? '#10b981' : '#ef4444'}`,
                      bgcolor: isCorrect ? '#ecfdf5' : '#fef2f2',
                      borderRadius: '14px',
                      mb: 2
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      {isCorrect ? (
                        <CheckCircleRoundedIcon sx={{ color: '#10b981', fontSize: '20px' }} />
                      ) : (
                        <CancelRoundedIcon sx={{ color: '#ef4444', fontSize: '20px' }} />
                      )}
                      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', fontFamily: 'Poppins' }}>
                        Your Answer
                      </Typography>
                    </Stack>
                    <Typography
                      component="div"
                      className="exam-rich-text"
                      sx={{ fontSize: '14px', fontWeight: 700, color: isAnswered ? (isCorrect ? '#10b981' : '#ef4444') : '#94a3b8', fontFamily: 'Poppins', textAlign: 'right' }}
                    >
                      {isAnswered ? <LatexRenderer text={studentAns} /> : 'Skipped'}
                    </Typography>
                  </Box>

                  {/* Box 2: Correct Answer (Only shown if incorrect or skipped) */}
                  {!isCorrect && q.correctAnswer && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        border: '1.5px solid #10b981',
                        bgcolor: '#ecfdf5',
                        borderRadius: '14px',
                        mb: 2
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <CheckCircleRoundedIcon sx={{ color: '#10b981', fontSize: '20px' }} />
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', fontFamily: 'Poppins' }}>
                          Correct Answer
                        </Typography>
                      </Stack>
                      <Typography
                        component="div"
                        className="exam-rich-text"
                        sx={{ fontSize: '14px', fontWeight: 700, color: '#10b981', fontFamily: 'Poppins', textAlign: 'right' }}
                      >
                        <LatexRenderer text={q.correctAnswer} />
                      </Typography>
                    </Box>
                  )}

                  {/* Box 3: Overall Feedback */}
                  {feedbackText && (
                    <Box
                      sx={{
                        border: '1.5px solid #3b82f6',
                        bgcolor: '#eff6ff',
                        borderRadius: '14px',
                        p: 2.25
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ChatBubbleOutlineRoundedIcon sx={{ color: '#3b82f6', fontSize: '18px' }} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 750, color: '#3b82f6', fontFamily: 'Poppins' }}>
                          Overall Feedback
                        </Typography>
                      </Box>
                      <Typography
                        component="div"
                        className="exam-rich-text"
                        sx={{ fontSize: '13px', color: '#475569', fontFamily: 'Poppins', lineHeight: 1.5 }}
                      >
                        <LatexRenderer text={feedbackText} />
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          } else {
            // Render Essay/Structured Layout using Accordion
            const qNumStr = `Question ${(q.questionNumber || idx + 1).toString().padStart(2, '0')}`;
            const scoreLabel = `${q.marksAwarded ?? 0}/${q.totalMarks ?? 0}`;
            const qPointsStyle = getPointsStyle(q.marksAwarded, q.totalMarks);

            return (
              <Accordion
                key={q.questionId || q.id || idx}
                sx={{
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '20px !important',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  overflow: 'hidden',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    borderColor: '#cbd5e1',
                    boxShadow: '0 4px 15px rgba(148, 163, 184, 0.05)'
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#64748b' }} />}
                  sx={{
                    px: { xs: 2.5, sm: 3.5 },
                    py: 1,
                    bgcolor: '#ffffff',
                    minHeight: '64px',
                    '& .MuiAccordionSummary-content': {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      flexWrap: 'wrap',
                      gap: 2
                    }
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mr: 'auto', flexWrap: 'wrap', gap: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1e293b', fontFamily: 'Poppins' }}>
                      {qNumStr}
                    </Typography>
                    <Chip
                      label="Written Answer"
                      variant="outlined"
                      sx={{
                        color: '#4f46e5',
                        borderColor: '#c7d2fe',
                        bgcolor: '#e0e7ff',
                        fontWeight: 650,
                        fontFamily: 'Poppins',
                        fontSize: '12px',
                        height: '28px'
                      }}
                    />
                  </Stack>

                  <Chip
                    label={`${scoreLabel} Points`}
                    sx={{
                      bgcolor: qPointsStyle.bg,
                      color: qPointsStyle.color,
                      fontWeight: 700,
                      fontFamily: 'Poppins',
                      fontSize: '12px',
                      height: '28px',
                      px: 0.5,
                      mr: 1
                    }}
                  />
                </AccordionSummary>

                <AccordionDetails sx={{ px: { xs: 2.5, sm: 4 }, pb: 4, pt: 1, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                  {/* Question Text */}
                  {q.questionText && q.questionText !== qNumStr && (
                    <Typography
                      component="div"
                      className="exam-rich-text"
                      sx={{
                        fontWeight: 600,
                        fontSize: '15px',
                        color: '#1e293b',
                        fontFamily: 'Poppins',
                        mb: 3,
                        lineHeight: 1.55,
                        mt: 2
                      }}
                    >
                      {q.questionType === 'match-two-sides' && (
                        <Typography sx={{ mb: 1.5, fontWeight: 700, fontSize: '15px', color: '#475569', fontFamily: 'Poppins' }}>
                          Match two sides question :
                        </Typography>
                      )}
                      <LatexRenderer text={q.questionText} />
                    </Typography>
                  )}

                  {/* Stack of Sub Parts */}
                  <Stack spacing={3.5} sx={{ mt: 2 }}>
                    {(q.markingBreakdown || []).map((part, pIdx) => {
                      const partStyle = getPointsStyle(part.marksAwarded, part.points);
                      const partIsFullyCorrect = part.points > 0 && part.marksAwarded === part.points;
                      const partIsPartiallyCorrect = part.points > 0 && part.marksAwarded > 0 && part.marksAwarded < part.points;
                      const partIsZeroPoints = part.points === 0;

                      const partScoreLabel = `${part.marksAwarded ?? 0}/${part.points ?? 0}`;

                      return (
                        <Box
                          key={part.id || pIdx}
                          sx={{
                            p: 3,
                            border: `1.5px solid ${partStyle.color}`,
                            bgcolor: partStyle.bg,
                            borderRadius: '18px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2.5
                          }}
                        >
                          {/* Sub Part Header */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontWeight: 750, fontSize: '15px', color: '#1e293b', fontFamily: 'Poppins' }}>
                              {part.level}
                            </Typography>

                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                bgcolor: partIsZeroPoints ? 'rgba(148, 163, 184, 0.15)' : (partIsFullyCorrect ? '#ecfdf5' : (partIsPartiallyCorrect ? '#fffbeb' : '#fef2f2')),
                                borderRadius: '999px',
                                px: 1.25,
                                py: 0.5,
                                height: '28px',
                                boxSizing: 'border-box'
                              }}
                            >
                              {partIsZeroPoints ? null : (partIsFullyCorrect ? (
                                <CheckCircleRoundedIcon sx={{ color: '#10b981', fontSize: '16px' }} />
                              ) : (
                                <CancelRoundedIcon sx={{ color: partIsPartiallyCorrect ? '#f59e0b' : '#ef4444', fontSize: '16px' }} />
                              ))}
                              <Typography
                                sx={{
                                  fontWeight: 750,
                                  fontSize: '12px',
                                  color: partStyle.color,
                                  fontFamily: 'Poppins'
                                }}
                              >
                                {partScoreLabel}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Your Answer Body */}
                          {part.studentAnswer && (
                            <Box>
                              <Typography sx={{ fontSize: '12.5px', color: '#64748b', fontWeight: 650, fontFamily: 'Poppins', mb: 1 }}>
                                Your Answer:
                              </Typography>
                              <Typography
                                component="div"
                                className="exam-rich-text"
                                sx={{ fontSize: '13.5px', color: '#1e293b', fontFamily: 'Poppins', lineHeight: 1.5 }}
                              >
                                <LatexRenderer text={part.studentAnswer} />
                              </Typography>
                            </Box>
                          )}

                          {/* Feedback Box */}
                          {part.feedback && (
                            <Box
                              sx={{
                                border: '1.5px solid #3b82f6',
                                bgcolor: '#eff6ff',
                                borderRadius: '12px',
                                p: 2
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <ChatBubbleOutlineRoundedIcon sx={{ color: '#3b82f6', fontSize: '18px' }} />
                                <Typography sx={{ fontSize: '13px', fontWeight: 750, color: '#3b82f6', fontFamily: 'Poppins' }}>
                                  Feedback
                                </Typography>
                              </Box>
                              <Typography
                                component="div"
                                className="exam-rich-text"
                                sx={{ fontSize: '13px', color: '#475569', fontFamily: 'Poppins', lineHeight: 1.45 }}
                              >
                                <LatexRenderer text={part.feedback} />
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>

                  {/* Overall Feedback for essay/structured question */}
                  {(q.feedback || q.overallFeedback) && (
                    <Box
                      sx={{
                        mt: 3.5,
                        border: '1.5px solid #3b82f6',
                        bgcolor: '#eff6ff',
                        borderRadius: '14px',
                        p: 2.25
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ChatBubbleOutlineRoundedIcon sx={{ color: '#3b82f6', fontSize: '18px' }} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 750, color: '#3b82f6', fontFamily: 'Poppins' }}>
                          Overall Feedback
                        </Typography>
                      </Box>
                      <Typography
                        component="div"
                        className="exam-rich-text"
                        sx={{ fontSize: '13px', color: '#475569', fontFamily: 'Poppins', lineHeight: 1.5 }}
                      >
                        <LatexRenderer text={q.feedback || q.overallFeedback} />
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          }
        })}
      </Stack>
    </Box>
  );
}
